import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalConfig, loginRequest, silentRequest } from '../config/msalConfig';
import TokenManager from '../utils/tokenManager';
import axios from '../api/axios';
import { toast } from 'react-toastify';
import { useNavigate } from "react-router-dom";
import api from '../api/axios';

// Create auth context
const MicrosoftAuthContext = createContext(null);
// Create MSAL instance but don't use it before initialization
const msalInstance = new PublicClientApplication(msalConfig);

// Auth provider
export const MicrosoftAuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [jwtToken, setJwtToken] = useState(null);
  const [userTeamData, setUserTeamData] = useState(null);
  const [isItUser, setIsItUser] = useState(false);

  // Initialize authentication system
  const initializeAuth = useCallback(async () => {
    setIsLoading(true);

    try {
      console.log('[Auth] Initializing authentication system...');

      // Initialize MSAL
      await msalInstance.initialize();
      setIsInitialized(true);

      // Clear any stale MSAL cache first
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        console.log('[Auth] Clearing stale MSAL cache before initialization');
        try {
          await msalInstance.clearCache();
        } catch (clearError) {
          console.warn('[Auth] Failed to clear cache:', clearError);
        }
      }

      // Check for existing valid token first
      const existingToken = TokenManager.getToken();
      if (existingToken) {
        console.log('[Auth] Found existing valid token, validating with backend...');

        // Restore user data from localStorage immediately
        const storedUserData = TokenManager.getUserData();
        if (storedUserData) {
          setUser(storedUserData);
          setUserTeamData(storedUserData.userTeamData);
          setIsItUser(storedUserData.isItUser || false);
          setJwtToken(existingToken);
          setIsAuthenticated(true);
          api.defaults.headers.common['Authorization'] = `Bearer ${jwtToken}`;
          console.log('[Auth] User data restored from localStorage');
        }

        try {
          // Validate token with backend to ensure it's still valid
          const response = await axios.get('/api/users/me');
          console.log('[Auth] Backend response:', response.data);

          if (response.data && response.data.success) {
            const userData = response.data.data;
            console.log('[Auth] Backend user data:', userData);

            // Only update user if we got valid data from backend
            if (userData && (userData.name || userData.email)) {
              setUser(userData);
              console.log('[Auth] Token validation successful, user data refreshed');
            } else {
              console.log('[Auth] Backend returned empty user data, keeping localStorage data');
            }
            return;
          }
        } catch (tokenError) {
          console.log('[Auth] Existing token validation failed, clearing all auth data');
          TokenManager.clearToken();
          // Clear state since token is invalid
          setUser(null);
          setUserTeamData(null);
          setJwtToken(null);
          setIsAuthenticated(false);
          // Clear MSAL cache as well
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            console.log('[Auth] Clearing stale MSAL accounts');
            for (const account of accounts) {
              await msalInstance.clearCache();
            }
          }
        }
      }

      // Don't automatically attempt silent token acquisition on init
      // User must explicitly click login button
      console.log('[Auth] Initialization complete, waiting for user action');

    } catch (error) {
      console.error('[Auth] Authentication initialization failed:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Acquire token silently (without user interaction)
  const acquireTokenSilently = useCallback(async () => {
    try {
      const account = msalInstance.getActiveAccount();
      if (!account) {
        throw new Error('No active account found');
      }

      console.log('[Auth] Attempting silent token acquisition...');
      const response = await msalInstance.acquireTokenSilent({
        ...silentRequest,
        account: account
      });

      console.log('[Auth] Silent token acquisition successful');
      await validateTokenWithBackend(response.accessToken, account.username);

    } catch (error) {
      console.error('[Auth] Silent token acquisition failed:', error);

      if (error instanceof InteractionRequiredAuthError) {
        console.log('[Auth] Interaction required for token acquisition');
      }

      throw error;
    }
  }, []);

  // Validate Microsoft token with backend and get JWT
  const validateTokenWithBackend = useCallback(async (msalToken, username) => {
    try {
      console.log('[Auth] Validating Microsoft token with backend...');

      const response = await axios.post('/auth/microsoft/validate', {
        token: msalToken,
        username: username
      });

      if (response.data && response.data.success) {
        const { token: jwtToken, user: userData, userTeamData } = response.data;

        let isItUserValue = false;
        try {
          const payload = JSON.parse(atob(jwtToken.split('.')[1]));
          isItUserValue = !!payload?.isItUser;
        } catch (_) {}

        // Calculate token expiration (default 24 hours if not provided)
        const expiresIn = 24 * 60 * 60; // 24 hours in seconds

        // Store token and user data using TokenManager
        TokenManager.setToken(jwtToken, expiresIn, { ...userData, userTeamData, isItUser: isItUserValue });
        setUser(userData);
        setUserTeamData(userTeamData);
        setIsItUser(isItUserValue);
        setJwtToken(jwtToken);
        setAccessToken(msalToken);
        api.defaults.headers.common['Authorization'] = `Bearer ${jwtToken || msalToken}`;
        setIsAuthenticated(true);

        console.log('[Auth] Backend validation successful, user authenticated');
        return { success: true, user: userData };
      } else {
        throw new Error(response.data?.message || 'Backend validation failed');
      }
    } catch (error) {
      console.error('[Auth] Backend validation failed:', error);
      throw error;
    }
  }, []);

  // Session timeout function
  const sessionTimeout = useCallback(() => {
    console.log('[Auth] Session timeout detected');
    setIsAuthenticated(false);
    setUser(null);
    setUserTeamData(null);
    setIsItUser(false);
    setAccessToken(null);
    setJwtToken(null);
    TokenManager.clearToken();
    sessionStorage.clear();
    toast.warning('Your session has expired. Please log in again.');
    navigate('/login');
  }, [navigate]);

  // Refresh token function (defined early to avoid hoisting issues)
  const refreshToken = useCallback(async () => {
    try {
      console.log('[Auth] Refreshing authentication token...');

      if (!msalInstance) {
        throw new Error('MSAL not initialized');
      }

      const account = msalInstance.getActiveAccount();
      if (!account) {
        throw new Error('No active account for token refresh');
      }

      // Force refresh the token
      const response = await msalInstance.acquireTokenSilent({
        ...silentRequest,
        account: account,
        forceRefresh: true
      });

      // Validate with backend
      await validateTokenWithBackend(response.accessToken, account.username);

      console.log('[Auth] Token refresh successful');
      return { success: true, token: TokenManager.getToken() };

    } catch (error) {
      console.error('[Auth] Token refresh failed:', error);

      // If refresh fails, logout user
      sessionTimeout();
      throw error;
    }
  }, [validateTokenWithBackend, sessionTimeout]);

  useEffect(() => {
    initializeAuth();

    // Handle redirect after login if needed
    const handleRedirectPromise = async () => {
      try {
        const response = await msalInstance.handleRedirectPromise();
        if (response) {
          console.log('[Auth] Redirect response received, validating with backend...');
          msalInstance.setActiveAccount(response.account);
          await validateTokenWithBackend(response.accessToken, response.account.username);
        }
      } catch (error) {
        console.error('Handle redirect promise failed', error);
      }
    };

    if (window.location.hash) {
      handleRedirectPromise();
    }
  }, [initializeAuth, validateTokenWithBackend]);

  // Set up event listeners for token refresh and logout
  useEffect(() => {
    const handleTokenRefreshRequired = async (event) => {
      try {
        console.log('[Auth] Token refresh requested');
        const result = await refreshToken();

        // Dispatch completion event
        window.dispatchEvent(new CustomEvent('auth:token:refresh:complete', {
          detail: {
            success: true,
            token: result.token,
            timestamp: event.detail?.timestamp
          }
        }));
      } catch (error) {
        console.error('[Auth] Token refresh failed:', error);

        // Dispatch failure event
        window.dispatchEvent(new CustomEvent('auth:token:refresh:complete', {
          detail: {
            success: false,
            error: error.message,
            timestamp: event.detail?.timestamp
          }
        }));
      }
    };

    const handleLogoutRequired = (event) => {
      console.log('[Auth] Logout requested:', event.detail);
      sessionTimeout();
    };

    const handleTokenRefreshSuggested = () => {
      // Proactive token refresh when token is close to expiry
      if (TokenManager.needsRefresh() && isAuthenticated) {
        console.log('[Auth] Proactive token refresh triggered');
        refreshToken().catch(error => {
          console.error('[Auth] Proactive token refresh failed:', error);
        });
      }
    };

    // Add event listeners
    window.addEventListener('auth:token:refresh:required', handleTokenRefreshRequired);
    window.addEventListener('auth:logout:required', handleLogoutRequired);
    window.addEventListener('auth:token:refresh:suggested', handleTokenRefreshSuggested);

    // Cleanup event listeners
    return () => {
      window.removeEventListener('auth:token:refresh:required', handleTokenRefreshRequired);
      window.removeEventListener('auth:logout:required', handleLogoutRequired);
      window.removeEventListener('auth:token:refresh:suggested', handleTokenRefreshSuggested);
    };
  }, [refreshToken, isAuthenticated, sessionTimeout]);

  // Login function
  const login = useCallback(async () => {
    try {
      if (!isInitialized) {
        toast.error('Authentication system not initialized. Please try again later.');
        return false;
      }

      setIsLoading(true);
      console.log('[Auth] Starting login process...');

      // Clear any stale MSAL cache first
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        console.log('[Auth] Clearing stale MSAL cache before login');
        try {
          await msalInstance.clearCache();
        } catch (clearError) {
          console.warn('[Auth] Failed to clear cache:', clearError);
        }
      }

      // Always trigger popup login for fresh authentication
      console.log('[Auth] Triggering popup login...');
      const loginResponse = await msalInstance.loginPopup(loginRequest);
      const account = loginResponse.account;
      msalInstance.setActiveAccount(account);

      // Use the token from login response
      console.log('[Auth] Login successful, validating with backend...');
      await validateTokenWithBackend(loginResponse.accessToken, account.username);

      console.log('[Auth] Login complete');
      toast.success('Login successful!');
      return { success: true };

    } catch (error) {
      console.error('[Auth] Login failed:', error);

      // Clear any partially created sessions
      try {
        await msalInstance.clearCache();
        TokenManager.clearToken();
      } catch (clearError) {
        console.warn('[Auth] Failed to clear after error:', clearError);
      }

      toast.error(`Login failed: ${error.message}`);
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, [isInitialized, validateTokenWithBackend]);

  // Logout function
  const logout = useCallback(async () => {
    try {
      console.log('[Auth] Starting logout process...');

      // Clear local state
      setIsAuthenticated(false);
      setUser(null);
      setUserTeamData(null);
      setIsItUser(false);
      setAccessToken(null);
      setJwtToken(null);

      // Clear stored tokens using TokenManager
      TokenManager.clearToken();
      sessionStorage.clear();

      // Clear MSAL cache
      try {
        await msalInstance.clearCache();
        console.log('[Auth] MSAL cache cleared');
      } catch (clearError) {
        console.warn('[Auth] Failed to clear MSAL cache:', clearError);
      }

      // Logout from MSAL
      if (msalInstance && isInitialized) {
        await msalInstance.logoutPopup({
          postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri
        });
      }

      console.log('[Auth] Logout successful');
      toast.success('Logged out successfully');
      navigate('/login');

    } catch (error) {
      console.error('[Auth] Logout failed:', error);
      toast.error(`Logout failed: ${error.message}`);
    }
  }, [navigate, isInitialized]);

  // Handle authentication callback from redirect
  const handleAuthCallback = useCallback(async () => {
    try {
      if (!msalInstance) {
        throw new Error('MSAL not initialized');
      }

      console.log('[Auth] Handling authentication callback...');
      const response = await msalInstance.handleRedirectPromise();

      if (response) {
        console.log('[Auth] Redirect response received, validating with backend...');
        msalInstance.setActiveAccount(response.account);
        await validateTokenWithBackend(response.accessToken, response.account.username);
        return { success: true };
      } else {
        console.log('[Auth] No redirect response found');
        return { success: false, error: 'No authentication response found' };
      }
    } catch (error) {
      console.error('[Auth] Callback handling failed:', error);
      return { success: false, error: error.message };
    }
  }, [validateTokenWithBackend]);

  // Context value
  const contextValue = {
    // State
    isAuthenticated,
    user,
    userTeamData,
    isItUser,
    isLoading,
    isInitialized,
    accessToken,
    jwtToken,

    // Methods
    login,
    logout,
    refreshToken,
    handleAuthCallback,
    sessionTimeout,

    // Utilities
    getToken: TokenManager.getToken,
    isTokenValid: TokenManager.isAuthenticated,
    getTokenExpiry: TokenManager.getTokenExpiry,
    extractUserInfo: TokenManager.extractUserInfo,
    validateTokenStructure: TokenManager.validateTokenStructure,
  };

  return (
    <MicrosoftAuthContext.Provider value={contextValue}>
      {children}
    </MicrosoftAuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useMicrosoftAuth = () => {
  const context = useContext(MicrosoftAuthContext);

  if (!context) {
    throw new Error('useMicrosoftAuth must be used within a MicrosoftAuthProvider');
  }

  return context;
};


export default MicrosoftAuthContext; 